import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { includesFolded } from "../utils/text";
import {
  IconOrders, IconLoader, IconAlert, IconSearch, IconBranch,
} from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";
import { useCustomPaymentMethods, customPaymentName } from "../hooks/useCustomPaymentMethods";

interface OrderListItem {
  id: string;
  number: string | null;
  status: string;
  order_type: string;
  total_minor: number;
  currency: string;
  opened_at: string;
  updated_at: string;
  closed_at: string | null;
  branch_name: string;
  employee_name: string | null;
  item_count: number;
  note: string | null;
}
interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  vat_rate: number;
  note: string | null;
}
interface Payment {
  id: string;
  method: string;
  provider: string | null;
  amount: number;
  currency: string;
  paid_at: string;
}
interface OrderDetail extends OrderListItem {
  subtotal: number;
  discount_total: number;
  rounding: number;
  note: string | null;
  items: OrderItem[];
  payments: Payment[];
}
interface Branch { id: string; name: string; }
interface OrderListPage { items: OrderListItem[]; total: number; }

type SortKey = "updated" | "branch" | "employee" | "total" | "opened" | "note";

const STATUS_BADGES: Record<string, string> = {
  open: "badge badge-yellow", paying: "badge badge-yellow",
  paid: "badge badge-green", cancelled: "badge badge-red", refunded: "badge badge-red",
};

function formatMoney(minor: number, currency: string): string {
  return new Intl.NumberFormat("cs-CZ", { style: "currency", currency, minimumFractionDigits: 0 }).format(minor / 100);
}

export default function OpenAccounts() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("all");
  const [sortKey, setSortKey] = useState<SortKey>("updated");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      params.set("limit", "100");
      params.set("sort", sortKey);
      params.set("order", sortDir);
      params.append("status", "open");
      params.append("status", "paying");
      if (filterBranch !== "all") params.set("branch_id", filterBranch);
      const [ords, brs] = await Promise.all([
        api.get<OrderListPage>(`/orders?${params}`).then(r => r.data),
        api.get<Branch[]>("/branches?limit=500").then(r => r.data).catch(() => []),
      ]);
      setOrders(ords.items);
      setBranches(brs);
    } catch (e) {
      setErr(apiError(e, t("accounts.loadError")));
    } finally {
      setLoading(false);
    }
  }, [filterBranch, sortKey, sortDir, t]);

  const statusLabel = (s: string) => t(`orders.statusLabels.${s}`, { defaultValue: s });
  const typeLabel = (s: string) => t(`orders.types.${s}`, { defaultValue: s });
  const customMethods = useCustomPaymentMethods();
  const paymentLabel = (p: { method: string; provider?: string | null }) => {
    const key = p.method === "card" && p.provider === "sumup"
      ? "sumup"
      : p.method === "other" && p.provider ? customPaymentName(customMethods, p.provider) : p.method;
    return t(`orders.pay.${key}`, { defaultValue: key });
  };
  const formatTimeAgo = (iso: string) => {
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return t("dashboard.timeJustNow");
    if (mins < 60) return t("dashboard.timeMinutesAgo", { count: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t("dashboard.timeHoursAgo", { count: hours });
    return t("dashboard.timeDaysAgo", { count: Math.floor(hours / 24) });
  };

  const COLUMNS: { label: string; sort: SortKey | null }[] = [
    { label: t("orders.colBranch"), sort: "branch" },
    { label: t("accounts.colEmployee"), sort: "employee" },
    { label: t("orders.colTotal"), sort: "total" },
    { label: t("orders.opened"), sort: "opened" },
    { label: t("orders.colUpdated"), sort: "updated" },
    { label: t("accounts.colItems"), sort: null },
    { label: t("orders.colNote"), sort: "note" },
  ];

  useEffect(() => { load(); }, [load]);

  async function showDetail(id: string) {
    setDetailLoading(true);
    setSelectedOrder(null);
    try {
      const { data } = await api.get<OrderDetail>(`/orders/${id}`);
      setSelectedOrder(data);
    } catch (e) {
      setErr(apiError(e, t("accounts.detailLoadError")));
    } finally {
      setDetailLoading(false);
    }
  }

  const filtered = orders.filter(o => {
    if (!search) return true;
    return includesFolded(o.note || "", search) || includesFolded(o.employee_name || "", search);
  });

  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><IconLoader size={32} className="muted" /></div>;
  }

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
        <div>
          <h1>{t("accounts.title")}</h1>
          <p>{t("accounts.subtitle")}</p>
        </div>
      </div>

      {err && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 20, background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <IconAlert size={16} /> {err}
        </div>
      )}

      <div className="card" style={{ padding: 16, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 220, maxWidth: 360 }}>
            <IconSearch size={18} className="muted" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
            <input placeholder={t("accounts.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} style={{ paddingLeft: 38, width: "100%" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 220 }}>
            <IconBranch size={18} className="muted" />
            <SearchableSelect
              value={filterBranch}
              onChange={setFilterBranch}
              options={[{ value: "all", label: t("dashboard.allBranches") }, ...branches.map(b => ({ value: b.id, label: b.name }))]}
              placeholder={t("dashboard.allBranches")}
              searchPlaceholder={t("dashboard.searchBranch")}
            />
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: "center", color: "var(--muted)" }}>
          <IconOrders size={48} className="muted" />
          <h3 style={{ marginTop: 16, fontSize: 18, fontWeight: 600 }}>{t("accounts.empty")}</h3>
          <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 8 }}>{t("accounts.emptyHint")}</p>
        </div>
      ) : (
        <div className="card" style={{ overflow: "auto" }}>
          <table>
            <thead>
              <tr>
                {COLUMNS.map(c => (
                  <th
                    key={c.label}
                    onClick={c.sort ? () => {
                      if (sortKey === c.sort) setSortDir(d => d === "asc" ? "desc" : "asc");
                      else { setSortKey(c.sort!); setSortDir("desc"); }
                    } : undefined}
                    style={c.sort ? { cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" } : undefined}
                  >
                    {c.label}{c.sort && sortKey === c.sort ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(o => (
                <tr key={o.id} onClick={() => showDetail(o.id)} style={{ cursor: "pointer" }}>
                  <td>{o.branch_name}</td>
                  <td>{o.employee_name || "—"}</td>
                  <td style={{ fontWeight: 600 }}>{formatMoney(o.total_minor, o.currency)}</td>
                  <td style={{ color: "var(--muted)", fontSize: 13 }}>{formatTimeAgo(o.opened_at)}</td>
                  <td style={{ color: "var(--muted)", fontSize: 13 }}>{formatTimeAgo(o.updated_at)}</td>
                  <td>{o.item_count}</td>
                  <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal */}
      {(selectedOrder || detailLoading) && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={() => setSelectedOrder(null)}>
          <div className="card" style={{ width: "100%", maxWidth: 800, maxHeight: "90vh", overflow: "auto", padding: 24 }} onClick={(e) => e.stopPropagation()}>
            {detailLoading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><IconLoader size={32} className="muted" /></div>
            ) : selectedOrder && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                  <div>
                    <h2 style={{ fontSize: 20, fontWeight: 600 }}>{t("accounts.account")} {selectedOrder.number || "—"}</h2>
                    <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 4 }}>{selectedOrder.branch_name} · {typeLabel(selectedOrder.order_type)}</p>
                  </div>
                  <button className="ghost" onClick={() => setSelectedOrder(null)}>{t("orders.close")}</button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20 }}>
                  <div>
                    <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>{t("accounts.itemsTitle")}</h3>
                    <table>
                      <thead><tr><th>{t("orders.colItem")}</th><th>{t("orders.colQty")}</th><th>{t("orders.colPrice")}</th><th>{t("orders.colSum")}</th></tr></thead>
                      <tbody>
                        {selectedOrder.items.map(it => (
                          <tr key={it.id}>
                            <td style={{ fontWeight: 600 }}>{it.name}{it.note && <div style={{ fontSize: 12, color: "var(--muted)" }}>{it.note}</div>}</td>
                            <td>{it.quantity}</td>
                            <td>{formatMoney(it.unit_price, selectedOrder.currency)}</td>
                            <td style={{ fontWeight: 600 }}>{formatMoney(it.line_total, selectedOrder.currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 6, fontSize: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "var(--muted)" }}>{t("orders.subtotal")}</span><span>{formatMoney(selectedOrder.subtotal, selectedOrder.currency)}</span></div>
                      {selectedOrder.discount_total > 0 && <div style={{ display: "flex", justifyContent: "space-between", color: "var(--red)" }}><span>{t("orders.discount")}</span><span>-{formatMoney(selectedOrder.discount_total, selectedOrder.currency)}</span></div>}
                      {selectedOrder.rounding !== 0 && <div style={{ display: "flex", justifyContent: "space-between" }}><span>{t("orders.rounding")}</span><span>{formatMoney(selectedOrder.rounding, selectedOrder.currency)}</span></div>}
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 18, fontWeight: 700, borderTop: "1px solid var(--border)", paddingTop: 8, marginTop: 4 }}>
                        <span>{t("orders.total")}</span><span>{formatMoney(selectedOrder.total_minor, selectedOrder.currency)}</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <div className="card" style={{ padding: 20, background: "var(--bg-elevated)" }}>
                      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{t("orders.info")}</h3>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
                        <Row label={t("orders.colStatus")} value={<span className={STATUS_BADGES[selectedOrder.status]}>{statusLabel(selectedOrder.status)}</span>} />
                        <Row label={t("orders.colBranch")} value={selectedOrder.branch_name} />
                        <Row label={t("accounts.colEmployee")} value={selectedOrder.employee_name || "—"} />
                        <Row label={t("orders.opened")} value={new Date(selectedOrder.opened_at).toLocaleString("cs-CZ")} />
                        {selectedOrder.note && <Row label={t("orders.colNote")} value={selectedOrder.note} />}
                      </div>
                    </div>
                    <div className="card" style={{ padding: 20, background: "var(--bg-elevated)" }}>
                      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{t("orders.payments")}</h3>
                      {selectedOrder.payments.length === 0 ? (
                        <div style={{ color: "var(--muted)", fontSize: 13 }}>{t("accounts.noPaymentsYet")}</div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
                          {selectedOrder.payments.map(p => (
                            <div key={p.id} style={{ display: "flex", justifyContent: "space-between" }}>
                              <span>{paymentLabel(p)}</span>
                              <span style={{ fontWeight: 600 }}>{formatMoney(p.amount, p.currency)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span style={{ color: "var(--muted)" }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: "right" }}>{value}</span>
    </div>
  );
}
